// Typed client for POST /api/cash-to-close/preview. The endpoint wraps
// /api/estimates/preview and layers closing-cost + prepaid + gap + DPA
// incentive derivation on top (SILL-39).

import type { Breakdown, EstimateRequest, Money } from "./agent-types";
import { ApiError, apiRequest } from "./api";
import { isAbortError } from "./estimates";

export type CashToClosePreviewRequest = {
  estimate: EstimateRequest;
  monthly_savings?: Money;
  applied_dpa_program_id?: string;
  seller_lender_credits_cents?: Money;
};

export type CashToClosePreviewResponse = {
  breakdown: Breakdown;
};

export class CashToClosePreviewError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "CashToClosePreviewError";
  }
}

export class CashToCloseUnauthorizedError extends CashToClosePreviewError {
  constructor(message = "Please sign in again to refresh cash-to-close.") {
    super(message);
    this.name = "CashToCloseUnauthorizedError";
  }
}

export class CashToCloseValidationError extends CashToClosePreviewError {
  constructor(public details: string) {
    super(details || "validation failed");
    this.name = "CashToCloseValidationError";
  }
}

export class CashToCloseServiceUnavailableError extends CashToClosePreviewError {
  constructor() {
    super("cash-to-close service unavailable");
    this.name = "CashToCloseServiceUnavailableError";
  }
}

export class CashToCloseNetworkError extends CashToClosePreviewError {
  constructor(public cause: unknown) {
    super("network error");
    this.name = "CashToCloseNetworkError";
  }
}

export type FetchCashToCloseOptions = {
  token: string;
  signal?: AbortSignal;
};

export async function fetchCashToClosePreview(
  req: CashToClosePreviewRequest,
  opts: FetchCashToCloseOptions,
): Promise<CashToClosePreviewResponse> {
  try {
    return await apiRequest<CashToClosePreviewResponse>(
      "/api/cash-to-close/preview",
      {
        method: "POST",
        body: req,
        token: opts.token,
        signal: opts.signal,
      },
    );
  } catch (err) {
    if (isAbortError(err)) throw err;
    if (err instanceof ApiError) {
      if (err.status === 401) {
        throw new CashToCloseUnauthorizedError(err.message);
      }
      if (err.status === 422 || err.status === 400) {
        throw new CashToCloseValidationError(err.message);
      }
      if (err.status === 503) {
        throw new CashToCloseServiceUnavailableError();
      }
      throw new CashToClosePreviewError(err.message);
    }
    throw new CashToCloseNetworkError(err);
  }
}
